import type { JournalEntry, JournalQuestData, QuestObjective } from '@roll1/shared';

/**
 * Q-5: the chain «follows from» of quests. `followsFrom` lives in the quest's data (GM only: the server never
 * sends it to a player). Everything here resolves it against the entries that exist NOW — a parent that was
 * deleted is simply no parent.
 */

const questData = (entry: JournalEntry): JournalQuestData => entry.data as JournalQuestData;

export interface QuestParent {
  readonly quest: JournalEntry;
  /** The objective of the parent this quest comes after (absent: after the quest as a whole). */
  readonly objective: QuestObjective | undefined;
}

export function questParent(entries: readonly JournalEntry[], quest: JournalEntry): QuestParent | null {
  if (quest.type !== 'quest') return null;
  const from = questData(quest).followsFrom;
  if (from === undefined || from === null) return null;
  const parent = entries.find((e) => e.id === from.questId && e.type === 'quest');
  if (parent === undefined) return null;
  const objective =
    from.objectiveId === undefined ? undefined : (questData(parent).objectives ?? []).find((o) => o.id === from.objectiveId);
  return { quest: parent, objective };
}

/** The quests that follow from this one. */
export function questChildren(entries: readonly JournalEntry[], questId: string): JournalEntry[] {
  return entries.filter((e) => e.type === 'quest' && questData(e).followsFrom?.questId === questId);
}

/** Every quest that comes after this one, however far down the chain (it can not be chosen as its parent). */
export function questDescendantIds(entries: readonly JournalEntry[], questId: string): Set<string> {
  const found = new Set<string>();
  const queue = [questId];
  while (queue.length > 0) {
    const at = queue.pop()!;
    for (const child of questChildren(entries, at)) {
      if (found.has(child.id)) continue;
      found.add(child.id);
      queue.push(child.id);
    }
  }
  return found;
}
